import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  TextField,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { WizardDialog } from '../WizardDialog';
import { WizardStep } from '../WizardStep';
import { useWizard } from '../../contexts/WizardContext';
import { ProgressSummaryPanel, SummarySection } from '../wizard-panels/ProgressSummaryPanel';
import { rangeosApiService } from '../../services/rangeos-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../../services/rangeos-environment-api';
import { basePrefixApi } from '../../services/api';

export interface ExportDraftToS3WizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (jobId: string) => void;
}

const wizardSteps = [
  { label: 'Select Source', description: 'Choose RangeOS instance and draft' },
  { label: 'Select Destination', description: 'Choose S3 environment' },
  { label: 'Configure Export', description: 'Set export options' },
  { label: 'Review & Export', description: 'Review and start export' }
];

// Step 1: Select Source RangeOS and Drafts
const SourceSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [environments, setEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [selectedDrafts, setSelectedDrafts] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        const rangeosEnvs = await rangeosEnvironmentService.getActiveEnvironments();
        setEnvironments(rangeosEnvs);
      } catch (error) {
        console.error('Failed to load RangeOS environments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, []);

  useEffect(() => {
    const loadDrafts = async () => {
      const sourceEndpoint = wizard.getData('sourceEndpoint');
      if (!sourceEndpoint) return;

      setLoadingDrafts(true);
      try {
        const draftsData = await rangeosApiService.listDesignDrafts(sourceEndpoint);
        setDrafts(draftsData);
      } catch (error) {
        console.error('Failed to load drafts:', error);
      } finally {
        setLoadingDrafts(false);
      }
    };

    loadDrafts();
  }, [wizard.getData('sourceEndpoint')]);

  // Initialize selected drafts from wizard data
  useEffect(() => {
    const saved = wizard.getData('selectedDrafts', []);
    setSelectedDrafts(saved);
  }, []);

  // Update validation whenever selection changes
  useEffect(() => {
    const sourceEndpoint = wizard.getData('sourceEndpoint');
    const isValid = Boolean(sourceEndpoint && selectedDrafts.length > 0);
    wizard.setStepValidation(0, isValid);
  }, [selectedDrafts, wizard.getData('sourceEndpoint')]);

  const handleDraftToggle = (draftId: string) => {
    const newSelection = selectedDrafts.includes(draftId)
      ? selectedDrafts.filter(id => id !== draftId)
      : [...selectedDrafts, draftId];

    setSelectedDrafts(newSelection);
    wizard.setData('selectedDrafts', newSelection);

    // Update draft names for display
    const names = newSelection
      .map(id => drafts.find(d => d.id === id)?.name)
      .filter(Boolean);
    wizard.setData('selectedDraftNames', names);
  };

  const handleSelectAll = () => {
    const draftsToSelect = filteredDrafts;
    const allFilteredIds = draftsToSelect.map(d => d.id);

    // Check if all filtered drafts are selected
    const allFilteredSelected = allFilteredIds.every(id => selectedDrafts.includes(id));

    if (allFilteredSelected) {
      // Deselect all filtered drafts
      const newSelection = selectedDrafts.filter(id => !allFilteredIds.includes(id));
      setSelectedDrafts(newSelection);
      wizard.setData('selectedDrafts', newSelection);
      const names = newSelection.map(id => drafts.find(d => d.id === id)?.name).filter(Boolean);
      wizard.setData('selectedDraftNames', names);
    } else {
      // Select all filtered drafts
      const newSelection = [...new Set([...selectedDrafts, ...allFilteredIds])];
      setSelectedDrafts(newSelection);
      wizard.setData('selectedDrafts', newSelection);
      const names = newSelection.map(id => drafts.find(d => d.id === id)?.name).filter(Boolean);
      wizard.setData('selectedDraftNames', names);
    }
  };

  // Filter drafts based on search query
  const filteredDrafts = drafts.filter(draft => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      draft.name?.toLowerCase().includes(query) ||
      draft.description?.toLowerCase().includes(query) ||
      draft.id?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Source RangeOS Instance and Drafts
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose the RangeOS instance and draft scenarios you want to export to S3.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Source RangeOS Environment</InputLabel>
        <Select
          value={wizard.getData('sourceEndpoint', '')}
          onChange={(e) => {
            wizard.setData('sourceEndpoint', e.target.value);
            setSelectedDrafts([]);
            wizard.setData('selectedDrafts', []);
          }}
          label="Source RangeOS Environment"
        >
          {environments.map((env) => (
            <MenuItem key={env.id} value={env.id}>
              {env.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {wizard.getData('sourceEndpoint') && (
        <Box>
          {loadingDrafts ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading drafts...</Typography>
            </Box>
          ) : drafts.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No drafts found in this RangeOS instance.
            </Alert>
          ) : (
            <>
              <TextField
                fullWidth
                placeholder="Search drafts by name, description, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
                size="small"
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Select Draft Scenarios ({selectedDrafts.length} selected, showing {filteredDrafts.length} of {drafts.length})
                </Typography>
                <Box>
                  <Chip
                    label={filteredDrafts.every(d => selectedDrafts.includes(d.id)) && filteredDrafts.length > 0 ? "Deselect All" : "Select All"}
                    onClick={handleSelectAll}
                    size="small"
                    color="primary"
                    variant="outlined"
                    disabled={filteredDrafts.length === 0}
                  />
                </Box>
              </Box>
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                {filteredDrafts.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No drafts match "{searchQuery}"
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {filteredDrafts.map((draft) => (
                      <ListItem
                        key={draft.id}
                        button
                        onClick={() => handleDraftToggle(draft.id)}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <input
                            type="checkbox"
                            checked={selectedDrafts.includes(draft.id)}
                            onChange={() => handleDraftToggle(draft.id)}
                            style={{ marginRight: 12 }}
                          />
                          <ListItemText
                            primary={draft.name}
                            secondary={draft.description || 'No description'}
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </>
          )}
        </Box>
      )}

      {selectedDrafts.length > 0 && (
        <Alert severity="info">
          Ready to export <strong>{selectedDrafts.length}</strong> draft{selectedDrafts.length > 1 ? 's' : ''}
        </Alert>
      )}
    </Box>
  );
};

// Step 2: Select S3 Destination
const DestinationSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [environments, setEnvironments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        const envs = await basePrefixApi.getEnvironmentsWithBuckets();
        setEnvironments(envs);
      } catch (error) {
        console.error('Failed to load environments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, []);

  // Update validation whenever target environment changes
  useEffect(() => {
    const targetEnvironmentId = wizard.getData('targetEnvironmentId');
    wizard.setStepValidation(1, Boolean(targetEnvironmentId));
  }, [wizard.getData('targetEnvironmentId')]);

  if (loading) {
    return <CircularProgress />;
  }

  const selectedEnv = environments.find(e => e.environmentId === wizard.getData('targetEnvironmentId'));

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select S3 Destination
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose the S3 environment where the draft scenarios will be exported.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>S3 Environment</InputLabel>
        <Select
          value={wizard.getData('targetEnvironmentId', '')}
          onChange={(e) => {
            const env = environments.find(env => env.environmentId === e.target.value);
            wizard.setData('targetEnvironmentId', e.target.value);
            wizard.setData('targetEnvironment', env?.prefix || ''); // Store prefix for backend
          }}
          label="S3 Environment"
        >
          {environments.map((env) => (
            <MenuItem key={env.environmentId} value={env.environmentId}>
              {env.environmentName} ({env.prefix}) - {env.bucketConfig.bucket}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedEnv && (
        <Alert severity="info">
          Draft scenarios will be exported to: <strong>{selectedEnv.bucketConfig.bucket}/{selectedEnv.prefix}/rangeos/drafts/</strong>
        </Alert>
      )}
    </Box>
  );
};

// Step 3: Configure Export Options
const ConfigureExportStep: React.FC = () => {
  const wizard = useWizard();

  // Step 2 is always valid (optional configuration)
  useEffect(() => {
    wizard.setStepValidation(2, true);
  }, []);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure Export Options
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure how the draft scenario should be exported.
      </Typography>

      <TextField
        fullWidth
        label="Export Filename (optional)"
        value={wizard.getData('exportFilename', '')}
        onChange={(e) => wizard.setData('exportFilename', e.target.value)}
        placeholder="Leave empty to use draft name"
        helperText="Custom filename for the exported JSON file (without .json extension)"
        sx={{ mb: 3 }}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        The draft will be exported as a JSON file containing the complete scenario definition, including all states, payloads, and references.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          What will be exported:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Draft metadata (name, description, version)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="All draft states and payloads" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Resource references (VMImages, Charts, etc.)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Scenario designer data" />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

// Step 4: Review and Confirm
const ReviewStep: React.FC = () => {
  const wizard = useWizard();
  const selectedDraftNames = wizard.getData('selectedDraftNames', []);
  const selectedDrafts = wizard.getData('selectedDrafts', []);

  // Review step is always valid
  useEffect(() => {
    wizard.setStepValidation(3, true);
  }, []);

  const summarySections: SummarySection[] = [
    {
      title: 'Source',
      items: [
        { label: 'RangeOS Instance', value: wizard.getData('sourceEndpoint') },
        { label: 'Drafts to Export', value: `${selectedDrafts.length} draft${selectedDrafts.length > 1 ? 's' : ''}` }
      ]
    },
    {
      title: 'Destination',
      items: [
        { label: 'S3 Environment', value: wizard.getData('targetEnvironment') },
        { label: 'Export Path', value: `${wizard.getData('targetEnvironment')}/rangeos/drafts/` }
      ]
    },
    {
      title: 'Export Options',
      items: [
        { label: 'Filename', value: wizard.getData('exportFilename') || 'Auto-generated from draft names' }
      ]
    }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Export Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review the export configuration before proceeding.
      </Typography>

      <ProgressSummaryPanel sections={summarySections} />

      {selectedDraftNames.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Drafts:
          </Typography>
          <List dense>
            {selectedDraftNames.map((name: string, idx: number) => (
              <ListItem key={idx}>
                <ListItemText primary={`${idx + 1}. ${name}`} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Alert severity="warning" sx={{ mt: 2 }}>
        <strong>Note:</strong> The draft scenarios will be exported with all their current resource references.
        If importing to a different RangeOS instance, you may need to use the Draft Validator to remap resources.
      </Alert>
    </Box>
  );
};

export const ExportDraftToS3Wizard: React.FC<ExportDraftToS3WizardProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const handleComplete = async (wizardData: Record<string, any>) => {
    const selectedDrafts = wizardData.selectedDrafts || [];

    try {
      // Use batch export endpoint for background processing
      const response = await rangeosApiService.batchExportDraftsToS3(
        wizardData.sourceEndpoint,
        {
          targetEnvironment: wizardData.targetEnvironment,
          draftIds: selectedDrafts,
          filenamePrefix: wizardData.exportFilename
        }
      );

      if (response.success && response.jobId) {
        console.log(`Batch export job created: ${response.jobId}`);

        if (onComplete) {
          // Return the job ID so the UI can track progress
          onComplete(response.jobId);
        }
      } else {
        throw new Error(response.message || 'Failed to create batch export job');
      }
    } catch (error: any) {
      console.error('Failed to start batch export:', error);
      throw error;
    }
  };

  return (
    <WizardDialog
      open={open}
      onClose={onClose}
      title="Export Draft Scenarios to S3"
      steps={wizardSteps}
      onComplete={handleComplete}
    >
      <WizardStep stepIndex={0}>
        <SourceSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={1}>
        <DestinationSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={2}>
        <ConfigureExportStep />
      </WizardStep>
      <WizardStep stepIndex={3}>
        <ReviewStep />
      </WizardStep>
    </WizardDialog>
  );
};
